import defaultModuleRouterHandler from './defaultModuleRouterHandler';
import getCurrentRoute from './getCurrentRoute';
import Module from 'mini-common-schema/lib/schema/Module';
import optionsModuleRouterHandler from './optionsModuleRouterHandler';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { composeCleanupRouteHandler, composeRouterHandler } from './composeRouterHandler';
import { endMailSearchSession } from 'mini-3s-mail-search-store';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { mailFolderCleanupRouteHandler, mailFolderRouteHandler } from './mailFolderRouteHandler';
import { mailReadingPaneRouteHandler } from './mailReadingPaneRouteHandler';
import { MailRouteType } from 'mini-mail-route-store/lib/schema/MailRouteType';
import { onActivityPaneEntryHandler, onActivityPaneExitHandler } from './activityPaneRouterHandler';
import { registerCalendarEventRoutes } from './registerCalendarEventRoutes';
import { registerPeopleCardRoutes } from 'mini-people-card-route-store';
import { RegisterRouteFunction, RouterOptions } from 'owa-router';
import { registerSearchRoutes } from 'mini-search-landing-view/lib/utils/registerSearchRoutes';
import { searchCleanupRouterHandler, searchRouterHandler } from './searchRouterHandler';
import {
    mailRouteCleanup,
    getHandlerForMailRoute,
} from 'mini-mail-route-store/lib/route-handlers/mailRouteHandler';
import {
    mailConversationReadingPaneRouteExitHandler,
    mailConversationReadingPaneRouteEntryHandler,
} from './mailConversationReadingPaneRouteHandler';
import {
    mailConversationReadingPaneAttachmentPreviewRouteEntryHandler,
    mailConversationReadingPaneAttachmentPreviewRouteExitHandler,
} from './mailConversationReadingPaneAttachmentPreviewRouteHandler';
import {
    feedbackModuleRouterHandler,
    feedbackModuleCleanupRouterHandler,
} from './feedbackModuleRouterHandler';
import {
    mailItemViewReadingPaneRouteEntryHandler,
    mailItemViewReadingPaneRouteExitHandler,
} from './mailItemReadingPaneRouteHandler';
import {
    mailItemReadingPaneAttachmentPreviewRouteEntryHandler,
    mailItemReadingPaneAttachmentPreviewRouteExitHandler,
} from './mailItemReadingPaneAttachmentPreviewRouteHandler';
import {
    mailComposeAttachmentPreviewRouteEntryHandler,
    mailComposeAttachmentPreviewRouteExitHandler,
} from './mailComposeAttachmentPreviewRouteHandler';

export const mailRouterOptions: RouterOptions = {
    routeGenerator: Promise.resolve(getCurrentRoute),
    vdir: 'mail',
    registerRoutes: initializeMailRoutes,
    defaultRoute: '/',
};

export async function initializeMailRoutes(registerRoute: RegisterRouteFunction) {
    if (isMiniFeatureEnabled('mini-unified-search')) {
        registerSearchRoutes(
            registerRoute,
            () => {
                switchModule(Module.Search);
            } /* RouteHandler */,
            endMailSearchSession
        );
    } else {
        registerRoute('/search', searchRouterHandler, searchCleanupRouterHandler);
    }

    registerRoute('/feedback', feedbackModuleRouterHandler, feedbackModuleCleanupRouterHandler);
    registerRoute(
        '/add-account',
        getHandlerForMailRoute(MailRouteType.addAccount),
        mailRouteCleanup
    );
    registerRoute(
        '/manage-accounts',
        getHandlerForMailRoute(MailRouteType.manageAccounts),
        mailRouteCleanup
    );
    registerRoute(
        '/manage-accounts/:userId',
        getHandlerForMailRoute(MailRouteType.manageAccounts),
        mailRouteCleanup
    );
    registerRoute('/activity', onActivityPaneEntryHandler, onActivityPaneExitHandler);
    registerRoute('/settings', optionsModuleRouterHandler);
    registerRoute('/settings/:subRoute', optionsModuleRouterHandler);
    /**
     * Adding this route to match the URL pattern on client-web
     * in order to support URLs like /settings/advertising/showpartnerlist/
     */
    registerRoute('/settings/:subRoute/:subRouteOption', optionsModuleRouterHandler);

    // Compose routes
    registerRoute('/compose', composeRouterHandler, composeCleanupRouteHandler);
    registerRoute('/compose/:draftId', composeRouterHandler, composeCleanupRouteHandler);
    registerRoute(
        '/compose/:draftId/attachment/:attachmentId',
        mailComposeAttachmentPreviewRouteEntryHandler,
        mailComposeAttachmentPreviewRouteExitHandler
    );

    // Support for the deep-linking URL retrieved from GraphAPIs
    // Few partner teams, like Todoist, query MS Graph to get the URL pattern to read an email.
    // Docs: https://docs.microsoft.com/en-us/previous-versions/office/office-365-api/api/version-2.0/mail-rest-operations#get-a-message
    // Following two URL patterns support this feature.
    // These need to be placed before "/:folderId" to avoid false positive route match
    registerRoute(
        '/jsmvvmdeeplink/',
        getHandlerForMailRoute(MailRouteType.deeplinkReadItem),
        mailRouteCleanup
    );
    registerRoute(
        '/deeplink/read/:itemId',
        getHandlerForMailRoute(MailRouteType.deeplinkReadItem),
        mailRouteCleanup
    );

    registerRoute('/:folderId', mailFolderRouteHandler, mailFolderCleanupRouteHandler);
    registerRoute(
        '/:folderId/id/:conversationId',
        mailConversationReadingPaneRouteEntryHandler,
        mailConversationReadingPaneRouteExitHandler
    );
    registerRoute(
        '/:folderId/id/:conversationId/attachment/:attachmentId',
        mailConversationReadingPaneAttachmentPreviewRouteEntryHandler,
        mailConversationReadingPaneAttachmentPreviewRouteExitHandler
    );

    /**
     * This route is currently being used by Webpush SW (service worker) to open mail reading pane.
     * 1. We need both conversation and item ids because Webpush SW doesn't know about conversation/item view preference for user.
     * 2. We need both formats of itemId (immutable Id and the mutable / entry / item Id) because currently immutable Id is rolling out
     *    and Webpush SW doesn't know whether the client is flighted immutable id or not. We could only use the immutable Id when it is
     *    flighted to WW100%.
     */
    registerRoute(
        '/:folderId/cId/:conversationId/iId/:itemId/immId/:immutableItemId',
        mailReadingPaneRouteHandler
    );
    registerRoute(
        '/:folderId/itemId/:itemId',
        mailItemViewReadingPaneRouteEntryHandler,
        mailItemViewReadingPaneRouteExitHandler
    );
    registerRoute(
        '/:folderId/itemId/:itemId/attachment/:attachmentId',
        mailItemReadingPaneAttachmentPreviewRouteEntryHandler,
        mailItemReadingPaneAttachmentPreviewRouteExitHandler
    );

    // register deeplink routes within mail module.
    // These deeplinks are spawned out of mail module still. But end goal is to have a standalone
    // deeplink bootstrap that does not require mail module boot
    // Can be moved to separate register function if this grows
    registerRoute(
        '/read/:itemId',
        getHandlerForMailRoute(MailRouteType.deeplinkReadItem),
        mailRouteCleanup
    );

    registerCalendarEventRoutes(registerRoute);
    registerPeopleCardRoutes(registerRoute);

    registerRoute('', defaultModuleRouterHandler);
}
